import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface RadioInputProps<TValue extends string = string>
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'type' | 'ref'
  > {
  labelFor?: string;
  label?: React.ReactNode;
  radioValue?: TValue;
  valueForEdit?: TValue;
  setAction?: (value: TValue | undefined) => void;
  onChange?: (value: TValue | undefined) => void;
  customClass?: string;
  labelClass?: string;
  customInputClasses?: string;
  value?: TValue;
}

const RadioInputImpl = forwardRef<HTMLInputElement, RadioInputProps<string>>(
  (
    {
      labelFor,
      label,
      radioValue,
      valueForEdit,
      setAction,
      onChange,
      customClass,
      labelClass,
      customInputClasses = '',
      value,
      ...rest
    },
    ref
  ) => {
    const labelClasses = twMerge(
      'flex items-center mb-3 mr-2 md:mr-4 text-gray-600 cursor-pointer',
      customClass,
    );

    const inputClasses = twMerge(
      'peer w-4 h-4 shrink-0 mr-2 accent-navy-800 cursor-pointer',
      customInputClasses,
    );

    const labelTextClasses = twMerge(
      'peer-checked:font-semibold text-gray-700 text-sm md:text-base',
      labelClass,
    );

    const handleChange = (): void => {
      setAction?.(radioValue);
      onChange?.(radioValue);
    };

    return (
      <label htmlFor={labelFor} className={labelClasses}>
        <input
          className={inputClasses}
          onChange={handleChange}
          type="radio"
          value={radioValue}
          id={labelFor}
          checked={radioValue === value || valueForEdit === value}
          ref={ref}
          {...rest}
        />
        <span className={labelTextClasses}>{label}</span>
      </label>
    );
  }
);

const RadioInput = RadioInputImpl as unknown as (<
  TValue extends string = string,
>(
  props: RadioInputProps<TValue> & { ref?: React.Ref<HTMLInputElement> }
) => React.ReactElement);

export { RadioInput };
export default RadioInput;
